import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import {
  FilterIds,
  FilterQueryArgs,
  PLAYBACK_SPEED_MODIFIER_OPTIONS,
  useFilterQueryParams,
} from "../../hooks/filter_query_params"
import { SEARCH_QUERY_KEYS, useSearchQueryParams } from "../../../../hooks/search_query_params"
import { useMapViewData } from "../../hooks/map_view_data"
import { ZoneType } from "@ciq/shared/utils/const"
import eventBus from "@ciq/shared/hooks/eventBus"
import { buildSelectorData } from "../../utils/selectorUtils"
import { ReactComponent as CalendarIcon } from "@ciq/shared/assets/icons/calendar_icon.svg"
import { ReactComponent as ClockIcon } from "@ciq/shared/assets/icons/clock_icon.svg"
import { Days } from "../../../../utils/date/manager"

const LeftPaneDataContext = createContext()

// Delay for DOM updates after hierarchy expansion before scrolling
const SCROLL_AFTER_EXPAND_DELAY_MS = 100

// Add constant for hour labels (same as right pane)
const HOURS = Array.from({ length: 24 }).map((_, i) => i)
const HOUR_LOOKUP = HOURS.map(hour => {
  if (hour === 0) return "12am"
  if (hour < 12) return `${hour}am`
  if (hour === 12) return `${hour}pm`
  return `${hour - 12}pm`
})

// Add constant for day abbreviations
const DAY_ABBREVIATIONS = Days.map(d => ({
  label: d.abbr,
  value: d.value,
}))

export const LeftPaneDataProvider = ({ children }) => {
  const data = LeftPaneData()
  return <LeftPaneDataContext.Provider value={data}>{children}</LeftPaneDataContext.Provider>
}

export const useLeftPaneData = () => {
  const context = useContext(LeftPaneDataContext)
  if (!context) {
    throw new Error("useLeftPaneDataContext must be used within LeftPaneDataProvider")
  }

  return context
}

// Export for testing purposes
export const LeftPaneData = () => {
  const [openIds, setOpenIds] = useState([])
  const { resources } = useMapViewData()
  const { zone, getZoneParentIds } = resources

  const { campusFlow, flowBuilding } = useFilterQueryParams()

  const searchData = useSearchData()
  const filterData = useFilterData()
  const datasetData = useDatasetData()
  const metricsData = useMetricsData()
  const listData = useListData(openIds)
  const footerData = useFooterData()

  // Stabilize getZoneParentIds so that it doesn't keep causing re-render issues
  const getZoneParentIdsRef = useRef(getZoneParentIds)
  getZoneParentIdsRef.current = getZoneParentIds

  // Listen for openIds changes via eventBus
  useEffect(() => {
    const handler = ids => {
      setOpenIds(prev => ids(prev))
    }

    eventBus.on("listview-ids-changed", handler)
    return () => eventBus.off("listview-ids-changed", handler)
  }, [])

  // Auto-expand parent hierarchy when zone selection changes
  useEffect(() => {
    if (!zone?.id) {
      return
    }

    // Skip auto-scroll when building is selected in campus flow mode
    if (campusFlow && flowBuilding) return

    const parentIds = getZoneParentIdsRef.current(zone.id)
    if (!parentIds || parentIds.size === 0) {
      return
    }

    // Add zone ID and all parent IDs to openIds (preserving existing)
    eventBus.emit("listview-ids-changed", prev => {
      const newIds = new Set(prev)
      newIds.add(zone.id)
      for (const parentId of parentIds) {
        newIds.add(parentId)
      }
      return Array.from(newIds)
    })

    // Scroll to selected element after DOM updates
    const scrollTimeout = setTimeout(() => {
      const element = document.getElementById(zone.id)
      if (!element) {
        return
      }

      // Find the header element within the zone container (the actual row, not the expanded children)
      const header = element.querySelector('.map-listview-collapsable-header') || element
      const rect = header.getBoundingClientRect()
      if (!rect) {
        return
      }
      // Check if element is outside visible viewport
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        header.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }, SCROLL_AFTER_EXPAND_DELAY_MS)

    return () => clearTimeout(scrollTimeout)
  }, [zone?.id, campusFlow, flowBuilding])

  return {
    search: searchData,
    filters: filterData,
    dataset: datasetData,
    metrics: metricsData,
    listData: listData,
    footer: footerData,
  }
}

const convertResourceData = value => {
  if (typeof value === "object") {
    return { label: value.name, value: value.id }
  }

  return { label: value, value: value }
}

const useSearchData = () => {
  const { resources } = useMapViewData()
  const { search, onSearch } = useSearchQueryParams()

  return {
    isSearching: resources.isFiltering,
    search,
    onSearch,
  }
}

const useFilterData = () => {
  const { zoneType, filters, setFilters } = useFilterQueryParams()
  const { resources } = useMapViewData()
  const { usages, buildings, floors } = resources

  const formattedUsages = useMemo(() => {
    return usages.map(convertResourceData)
  }, [usages])

  const formattedBuildings = useMemo(() => {
    return buildings.map(convertResourceData)
  }, [buildings])

  const formattedFloors = useMemo(() => {
    return floors.map(convertResourceData)
  }, [floors])

  const viewTypeData = useMemo(() => {
    return {
      values: [
        {
          selected: zoneType === ZoneType.BUILDING,
          label: "Building View",
          value: ZoneType.BUILDING,
        },
        {
          selected: zoneType === ZoneType.DEFAULT,
          label: "End Zone View",
          value: ZoneType.DEFAULT,
        },
      ],
      onChange: newZoneType => {
        setFilters({ [FilterQueryArgs.ZONE_TYPE]: newZoneType.value })
      },
    }
  }, [setFilters, zoneType])

  const usageFilterData = useMemo(() => {
    return buildFilterData(
      "Usages",
      formattedUsages,
      filters?.usages,
      FilterQueryArgs.USAGES,
      setFilters
    )
  }, [filters?.usages, setFilters, formattedUsages])

  const buildingFilterData = useMemo(() => {
    return buildFilterData(
      "Buildings",
      formattedBuildings,
      filters?.buildings,
      FilterQueryArgs.BUILDINGS,
      setFilters
    )
  }, [formattedBuildings, filters?.buildings, setFilters])

  const floorFilterData = useMemo(() => {
    return buildFilterData(
      "Floors",
      formattedFloors,
      filters?.floors,
      FilterQueryArgs.FLOORS,
      setFilters
    )
  }, [filters?.floors, formattedFloors, setFilters])

  return {
    viewType: viewTypeData,
    usages: usageFilterData,
    buildings: buildingFilterData,
    floors: floorFilterData,
  }
}

const useDatasetData = () => {
  const { mapDisplayType, days, hour, playbackSpeedModifier, setFilters } = useFilterQueryParams()

  return useMemo(() => {
    const options = [
      { icon: <CalendarIcon />, label: "Semester", value: FilterIds.Options.SEMESTER },
      { icon: <ClockIcon />, label: "Hourly", value: FilterIds.Options.HOURLY },
    ]

    const current = options.find(opt => opt.value === mapDisplayType) || options[0]

    const onChange = selected => {
      setFilters({ [FilterQueryArgs.OPTION]: selected.value })
    }

    // Build hourly controls data
    const dayValues = DAY_ABBREVIATIONS.map(day => ({
      ...day,
      selected: days ? days.includes(day.value) : false,
    }))

    const speedOptions = PLAYBACK_SPEED_MODIFIER_OPTIONS.map(speed => ({
      label: `${speed}x`,
      value: speed,
    }))

    return {
      dataset: {
        isHourly: mapDisplayType === FilterIds.Options.HOURLY,
        current,
        options,
        onChange,
      },
      hourlyControls: {
        days: {
          values: dayValues,
          onChange: selectedDays => {
            setFilters({ [FilterQueryArgs.DAYS]: selectedDays.map(d => d.value) })
          },
        },
        hour: {
          current: hour || 0,
          setHour: newHour => {
            setFilters({ [FilterQueryArgs.HOUR]: newHour })
          },
          label: HOUR_LOOKUP[hour || 0],
        },
        playback: {
          speedModifier: playbackSpeedModifier || 1,
          setPlaybackSpeed: newSpeed => {
            setFilters({ [FilterQueryArgs.PLAYBACK_SPEED_MODIFIER]: newSpeed })
          },
          speedOptions,
        },
      },
    }
  }, [mapDisplayType, days, hour, playbackSpeedModifier, setFilters])
}

const useMetricsData = () => {
  const { mapDisplayType, semesterMetric, utilizationMetric, setFilters } = useFilterQueryParams()
  const { semesterSummary, hourlySummary } = useMapViewData()

  // ignore for dep arrays
  const setFiltersRef = useRef(setFilters)

  return useMemo(() => {
    const isSemester = mapDisplayType === FilterIds.Options.SEMESTER || !mapDisplayType
    const metric = isSemester ? semesterMetric : utilizationMetric
    const table = isSemester ? semesterSummary?.table : hourlySummary?.table
    const metricKey = isSemester
      ? FilterQueryArgs.SEMESTER_METRIC
      : FilterQueryArgs.UTILIZATION_METRIC

    const selectorData = buildSelectorData(metric, table, metricKey, setFiltersRef.current)

    return {
      current: selectorData.value,
      options: selectorData.options,
      onChange: selectorData.onChange,
    }
  }, [
    mapDisplayType,
    semesterMetric,
    utilizationMetric,
    semesterSummary?.table,
    hourlySummary?.table,
  ])
}

/**
 * @typedef {Object} FilterDataResult
 * @property {string} label - Display label for the filter category
 * @property {Array<{label: string, value: string}>} options - Available filter options not currently selected
 * @property {Array<{label: string, value: string}>} values - Currently selected filter values
 * @property {(values: Array<string>) => void} onChange - Function to update filter selections
 */

/**
 * Builds filter data by separating available options from selected values.
 * Includes an onChange handler for updating filter selections.
 *
 * @param {string} label - The label for the selector
 * @param {Array<{label: string, value: string}>} data - The complete list of filter items
 * @param {Array<string>} filters - Array of selected filter values
 * @param {string} key - The filter key for identifying which filter to update
 * @param {Function} [setFilters] - Optional function to update filter state
 * @returns {FilterDataResult} Object containing label, separated options, values, and onChange handler
 */
const buildFilterData = (label, data, filters, key, setFilters) => {
  return {
    label,
    options: data?.filter(resource => !filters.includes(resource.value)),
    values: data?.filter(resource => filters.includes(resource.value)),
    onChange: values => {
      setFilters && setFilters({ [key]: values })
    },
  }
}

const useListData = openIds => {
  const { resources } = useMapViewData()
  const { zone, hierarchy, zoneIds } = resources
  const { isSearching } = useSearchQueryParams()
  const { setResourceId } = useFilterQueryParams()

  // stable setState. use ref to avoid dependency arrays
  const setResourceIdRef = useRef(setResourceId)

  return {
    isSearching,
    hasData: hierarchy?.length > 0 ?? false,
    resources: hierarchy,
    resourceId: zone?.id,
    set: useCallback(zone => {
      // don't forget that this is not a real "zone" object but a modified one for the list view
      // if you need anything from it, look up the real zone object like i'm doing for the campus
      // handling
      if (zone?.type === ZoneType.SCHOOL) {
        return
      }

      if (zone?.type === ZoneType.CAMPUS) {
        eventBus.emit("listview-campus-selected", zone.id)
        return
      }

      setResourceIdRef.current(zone?.id)

      if (zone?.id) {
        eventBus.emit("listview-resource-selected", {
          id: zone.id,
          name: zone.name,
          type: zone.type,
          source: "list",
        })
      }
    }, []),
    isOpen: useCallback(
      zoneId => {
        return openIds.includes(zoneId)
      },
      [openIds]
    ),
    isVisible: useCallback(
      zoneId => {
        if (zoneIds == null) {
          return true
        }

        return zoneIds.has(zoneId)
      },
      [zoneIds]
    ),
    toggleOpen: useCallback(
      zoneId => {
        if (openIds.includes(zoneId)) {
          eventBus.emit("listview-ids-changed", prev => [...prev.filter(id => id !== zoneId)])
        } else {
          eventBus.emit("listview-ids-changed", prev => [...prev, zoneId])
        }
      },
      [openIds]
    ),
  }
}

/**
 * Clear All Filters button behavior.
 * Clears: Buildings, Floors, Usages, Search
 * Preserves: Semester, Dataset (Map Display Type), View Toggle (Zone Type)
 */
const useFooterData = () => {
  const { setFilters } = useFilterQueryParams()

  const clearAllFilters = useCallback(() => {
    // Emit event to clear the search bar's internal text state
    eventBus.emit("clear-search")

    // Clear URL params for filters and search
    setFilters(
      {
        [FilterQueryArgs.BUILDINGS]: [],
        [FilterQueryArgs.USAGES]: [],
        [FilterQueryArgs.FLOORS]: [],
      },
      {
        additionalKeysToSkip: SEARCH_QUERY_KEYS,
      }
    )
  }, [setFilters])

  return {
    clearAllFilters,
  }
}
